import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {isEmpty} from 'lodash';

import MotifDropdownItem from '@ey/motif-react-wrapper/DropdownItem';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import MotifInlineMessage from '@ey/motif-react-wrapper/InlineMessage';
import {
	editorIcModeEdit24px,
	hardwareIcKeyboardArrowDown24px,
	hardwareIcKeyboardArrowUp24px
} from '@ey/motif-react-wrapper/assets/icons';

import {labels} from '../../../../util/utils';
import {quickFiltersIcon} from '../../../../util/svgIcons';

import Button from '../../../CoreWrappers/Button';
import Input from '../../../CoreWrappers/Input';
import FiltersModal from './FiltersModal';
import EmptyMessage from '../../../Common/Messages/EmptyMessage';
import Ellipsis from '../../../Common/Ellipsis/Ellipsis';
import {clearErrorsAndSetModal} from '../../../../actions/erroractions';
import {
	getQuickFilters,
	addQuickFilter,
	editQuickFilter,
	deleteQuickFilter,
	setOrgChart,
	resetNewFlag
} from '../../../../actions/Project/orgStructureAction';
import Dropdown from '../../../CoreWrappers/Dropdown';
import DropdownFooter from '../../DropdownFooter';

const QuickFiltersDropdown = (props) => {
	const ddlId = 'quick-filter-dropdown';
	const ddlTrigger = 'quick-filter-dropdown-trigger';
	const {projectId} = props;
	const maxLengthInput = 100;
	const maxQuickFilterCount = 10;

	const dispatch = useDispatch();
	const orgChart = useSelector((store) => store.orgChart);
	const quickFilters = useSelector((store) => store.quickFilters);
	const dashboardEntities = useSelector((store) => store.dashboardContext.entities);

	const [openDropdown, setOpenDropdown] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [ddlLabel, setddlLabel] = useState(labels.quickFilter);
	const [isCreate, setIsCreate] = useState(false);
	const [inputError, setInputError] = useState(false);
	const [entityError, setEntityError] = useState(false);
	const [filterName, setFilterName] = useState('');
	const [selectedFilter, setSelectedFilter] = useState({});
	const [editFilterId, setEditFilterId] = useState('');
	const [selectedEntities, setSelectedEntities] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const userClick = useRef(false);
	const element = useRef(null);

	useEffect(() => {
		const scrollbar = element?.current?.closest('.customScrollbar');

		const hideOnScroll = () => setOpenDropdown(false);

		if (scrollbar) scrollbar.addEventListener('scroll', hideOnScroll);
		return () => {
			scrollbar?.removeEventListener('scroll', hideOnScroll);
		};
	}, []);

	useEffect(() => {
		dispatch(getQuickFilters(projectId));
	}, []);

	useEffect(() => {
		onOrgChartChange();
	}, [orgChart, dashboardEntities]);

	const onOrgChartChange = () => {
		if (!userClick.current) {
			setddlLabel(labels.quickFilter);
		} else {
			if (isSpecificFilter()) {
				setddlLabel(selectedFilter ? selectedFilter.name : labels.quickFilter);
			} else {
				setddlLabel(labels.quickFilter);
			}
		}
	};

	const isSpecificFilter = () => {
		if (selectedFilter.orgChart === undefined) {
			return false;
		}

		let isSpecificFilterId = true;
		let entitiesCheck = dashboardEntities.filter((x) => x.checked);

		if (entitiesCheck.length > 0) {
			entitiesCheck = [...new Set([...entitiesCheck])];
		}

		let orgChartSelected = selectedFilter.orgChart.filter((x) => x.checked);

		orgChartSelected.forEach((orgChart) => {
			if (entitiesCheck.filter((x) => x.id === orgChart.id).length === 0) {
				isSpecificFilterId = false;
			}
		});

		return isSpecificFilterId && entitiesCheck.length === orgChartSelected.length;
	};

	const setModal = (isOpen) => {
		setShowModal(isOpen);
		dispatch(clearErrorsAndSetModal(isOpen));
	};

	const isValid = (selectedNodes) => {
		let isValid = true;
		if (filterName.length === 0) {
			setInputError(true);
			isValid = false;
		} else {
			setInputError(false);
		}

		if (!areEntitiesSelected(selectedNodes)) {
			setEntityError(true);
			isValid = false;
		} else {
			setEntityError(false);
		}
		return isValid;
	};

	const allowedToCreate = () => {
		return !areQuickFilters() || quickFilters?.filters?.length < maxQuickFilterCount;
	};

	const areQuickFilters = () => {
		return quickFilters && !isEmpty(quickFilters.filters);
	};

	const setFilter = (filter) => {
		let org = _.cloneDeep(orgChart);
		for (let i = 0; i < org.length; i++) {
			const exists = filter.orgChart.find((c) => c.id === org[i].id);
			org[i].checked = exists ? exists.checked : false;
		}

		setSelectedFilter(filter);
		setddlLabel(filter.name);
		dispatch(setOrgChart(org));
		setOpenDropdown(false);
	};

	const saveFilter = (selectedNodes) => {
		const nodes = selectedNodes.map((n) => {
			return {
				id: n.id,
				entityLevel: n.entityLevel,
				entityName: n.label,
				isChecked: n.checked,
				parentEntityId: n.parentId
			};
		});

		const body = {
			name: filterName,
			orgChart: nodes
		};

		dispatch(
			isCreate
				? addQuickFilter(projectId, body, filterName, selectedNodes)
				: editQuickFilter(projectId, editFilterId, quickFilters?.quickFilterId, filterName, selectedNodes, body)
		)
			.then(() => {
				resetValues();
				userClick.current = true;
				setFilter({
					name: filterName,
					orgChart: selectedNodes
				});
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const resetValues = () => {
		setIsLoading(false);
		setInputError(false);
		setEntityError(false);
		setModal(false);
		setFilterName('');
		setSelectedFilter({});
		setEditFilterId('');
	};

	const getMiddleSection = () => {
		return (
			<StyledMiddleSection className="middle-section">
				<Input
					id={'qfilterTextInputId'}
					disabled={isLoading}
					label={labels.quickFilterInput}
					maxLength={maxLengthInput}
					required={true}
					errorMessage={inputError ? labels.required : null}
					value={filterName ?? ''}
					onChange={handlerOnInputChange}
					clearButtonTitle={labels.clear}
				/>
			</StyledMiddleSection>
		);
	};

	const getFooterSection = () => {
		return entityError ? (
			<MotifInlineMessage variant="error" className="error-text">
				<Ellipsis content={labels.oneRequired} lines={2} />
			</MotifInlineMessage>
		) : (
			<></>
		);
	};

	const isSelected = (filter) => {
		return filter.filterId === selectedFilter?.filterId || filter?.isNew;
	};

	const areEntitiesSelected = (entitiesList) => {
		//TODO: change this too when root is added to modal.
		return entitiesList.some((o) => o.checked);
	};

	const handlerOnModalOk = (selectedNodes) => {
		if (!isLoading && isValid(selectedNodes)) {
			setIsLoading(true);
			saveFilter(selectedNodes);
		}
	};

	const handlerOnModalHide = useCallback(() => {
		setSelectedEntities([...dashboardEntities]);
		resetValues();
	}, []);

	const handlerOnModalDelete = () => {
		if (!isLoading) {
			setIsLoading(true);
			dispatch(deleteQuickFilter(projectId, editFilterId, quickFilters?.quickFilterId))
				.then(() => {
					resetValues();
					setddlLabel(labels.quickFilter);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const handlerOnEditFilterClick = (filter) => {
		setIsCreate(false);
		setOpenDropdown(false);
		setModal(true);
		setFilterName(filter.name);
		setSelectedEntities(filter.orgChart);
		setEditFilterId(filter.filterId);
	};

	const handlerOnNewFilterClick = () => {
		if (allowedToCreate()) {
			setIsCreate(true);
			setSelectedEntities([...dashboardEntities]);
			setOpenDropdown(false);
			setModal(true);
		}
	};

	const handlerOnSelectFilter = (filterId) => {
		userClick.current = true;
		dispatch(resetNewFlag());
		setFilter(quickFilters.filters.find((f) => f.filterId === filterId));
	};

	const handlerOnCloseDropDown = () => setOpenDropdown(false);

	const handlerOnToggleDropDown = () => setOpenDropdown(!openDropdown);

	const handlerOnInputChange = (e) => {
		setFilterName(e.target.value);
		setInputError(value?.length === 0);
	};

	const handlerOnItemChecked = (entitiesList) => {
		setEntityError(!areEntitiesSelected(entitiesList));
	};

	const handlerOnAllItemsChecked = (entitiesList) => {
		setEntityError(!areEntitiesSelected(entitiesList));
	};

	const renderTrigger = () => {
		const props = {
			svg: quickFiltersIcon,
			label: ddlLabel,
			title: ddlLabel,
			onClick: (e) => handlerOnToggleDropDown(e),
			variant: 'text'
		};

		return (
			<Button {...props}>
				<MotifIcon src={openDropdown ? hardwareIcKeyboardArrowUp24px : hardwareIcKeyboardArrowDown24px} />
			</Button>
		);
	};

	return (
		<StyledQuickFiltersDropdown innerRef={element}>
			<Dropdown
				id={ddlId}
				open={openDropdown}
				aria-labelledby={ddlTrigger}
				handleClickOutside={(e) => handlerOnCloseDropDown(e)}
				trigger={renderTrigger()}
				reversed
				topfix
			>
				{areQuickFilters() && (
					<div className="customScrollbar">
						{quickFilters.filters.map((filter) => {
							return (
								<StyledQuickFilterItem
									key={filter.filterId}
									option={filter.filterId}
									className={isSelected(filter) ? 'item-selected' : ''}
								>
									<div
										className="ellipsis-one-line filter-name-item"
										title={filter.name}
										onClick={() => {
											handlerOnSelectFilter(filter.filterId);
										}}
									>
										{filter.name}
									</div>
									<MotifIcon
										src={editorIcModeEdit24px}
										title={labels.editFilterIconTitleTip}
										onClick={() => {
											handlerOnEditFilterClick(filter);
										}}
									/>
								</StyledQuickFilterItem>
							);
						})}
					</div>
				)}
				{!areQuickFilters() && (
					<section className="empty-quick-filter">
						<EmptyMessage title={labels.noFiltersCreatedTitle} subTitle={labels.noFiltersCreatedSubTitle} />
					</section>
				)}
				<DropdownFooter>
					<div className={allowedToCreate() ? 'create-new-filter' : 'create-new-filter no-action'}>
						<Button
							onClick={handlerOnNewFilterClick}
							variant={'text-alt'}
							label={labels.createFilterTitle}
							disabled={!allowedToCreate()}
						/>
					</div>
				</DropdownFooter>
			</Dropdown>
			<FiltersModal
				orgChart={orgChart}
				selectedEntities={selectedEntities}
				isLoading={isLoading}
				showModal={showModal}
				modalTitle={isCreate ? labels.createFilterTitle : labels.editFilterTitle}
				primaryButtonLabel={isCreate ? labels.createFilterButton : labels.saveChanges}
				secondaryButtonLabel={labels.cancelLabel}
				auxiliaryButtonLabel={isCreate ? '' : labels.delete}
				instructionalHeader={isCreate ? labels.createFilterHeaderText : labels.editFilterHeaderText}
				instructionalText={labels.quickFilterSubHeaderText}
				onModalOk={handlerOnModalOk}
				onModalHide={handlerOnModalHide}
				onAuxiliaryButtonClick={handlerOnModalDelete}
				onItemChecked={handlerOnItemChecked}
				onAllItemsChecked={handlerOnAllItemsChecked}
				middleSection={getMiddleSection()}
				footerSection={getFooterSection()}
			/>
		</StyledQuickFiltersDropdown>
	);
};

QuickFiltersDropdown.propTypes = {
	projectId: PropTypes.string
};

export default QuickFiltersDropdown;

const StyledQuickFiltersDropdown = styled.section`
	--pad-h: 1.5rem;
	--pad-v: 2rem;
	min-width: 0;
	display: flex;
	.motif-dropdown,
	.motif-dropdown .motif-button {
		display: flex;
		min-width: 0;
	}
	.empty-quick-filter {
		text-align: center;
		.empty-message {
			padding: var(--pad-h);
			margin: 0;
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
			p {
				margin: 0;
			}
		}
	}
`;

const StyledMiddleSection = styled.section`
	font-size: 1.3rem;
	max-width: 35%;
	> section {
		min-height: 4rem;
	}
	.motif-inline-message-content {
		font-size: 1.3rem;
		color: ${(props) => props.theme.color[`red600`]};
	}
	.motif-inline-message svg {
		margin-right: 4px;
	}
	.motif-label {
		font-size: 1.3rem;
	}
`;

const StyledQuickFilterItem = styled(MotifDropdownItem)`
	max-width: var(--px-300);
	.motif-icon {
		flex-shrink: 0;
	}
`;
